import type { BrowserKey } from '../utils/constants';

import { Collapse } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { isMac } from '@vestigas/utils';

import { BROWSER_KEYS } from '../utils';

import { ChromeIcon } from './ChromeIcon';
import { FirefoxIcon } from './FirefoxIcon';
import { SafariIcon } from './SafariIcon';

type BrowserMeta = {
  readonly color: string;
  readonly Icon: (props: { readonly className?: string }) => JSX.Element;
  readonly label: string;
};

const BROWSER_META = {
  chrome: { color: '#4285F4', Icon: ChromeIcon, label: 'Chrome' },
  firefox: { color: '#FF7139', Icon: FirefoxIcon, label: 'Firefox' },
  safari: { color: '#1C8EF9', Icon: SafariIcon, label: 'Safari' },
} satisfies Record<BrowserKey, BrowserMeta>;

type P = {
  readonly expandedBrowser: BrowserKey | null;
  readonly isOpen: boolean;
  readonly toggleBrowser: (browser: BrowserKey) => void;
};

/**
 * Collapsible per-browser instructions for exporting a HAR file. Each browser
 * has its own static translation keys (Chrome/Firefox/Safari) so i18next-cli
 * extraction stays reliable.
 */
export function HarInstructions({ expandedBrowser, isOpen, toggleBrowser }: P) {
  const { t } = useTranslation('bugReport');

  const devToolsShortcut = isMac ? '⌘ + ⌥ + I' : 'Ctrl + Shift + I';

  const steps: Record<BrowserKey, readonly string[]> = {
    chrome: [
      t(($) => $.dialog.har.instructionsChromeStep1, {
        shortcut: devToolsShortcut,
      }),
      t(($) => $.dialog.har.instructionsChromeStep2),
      t(($) => $.dialog.har.instructionsChromeStep3),
      t(($) => $.dialog.har.instructionsChromeStep4),
    ],
    firefox: [
      t(($) => $.dialog.har.instructionsFirefoxStep1, {
        shortcut: devToolsShortcut,
      }),
      t(($) => $.dialog.har.instructionsFirefoxStep2),
      t(($) => $.dialog.har.instructionsFirefoxStep3),
      t(($) => $.dialog.har.instructionsFirefoxStep4),
      t(($) => $.dialog.har.instructionsFirefoxStep5),
    ],
    safari: [
      t(($) => $.dialog.har.instructionsSafariStep1),
      t(($) => $.dialog.har.instructionsSafariStep2),
      t(($) => $.dialog.har.instructionsSafariStep3),
      t(($) => $.dialog.har.instructionsSafariStep4),
    ],
  };

  return (
    <Collapse in={isOpen}>
      <div className="pb-2 text-xs text-tiga-text-default">
        <div className="flex flex-wrap gap-2">
          {BROWSER_KEYS.map((key) => {
            const { color, Icon, label } = BROWSER_META[key];
            const isSelected = expandedBrowser === key;

            return (
              <button
                key={key}
                className="flex h-8 cursor-pointer items-center gap-1 rounded-full border pr-3 pl-1 font-medium transition-colors"
                style={
                  isSelected
                    ? {
                        backgroundColor: color,
                        borderColor: color,
                        color: 'white',
                      }
                    : { borderColor: `${color}66`, color }
                }
                type="button"
                onClick={() => toggleBrowser(key)}
              >
                <Icon className="size-6 shrink-0" />
                {label}
              </button>
            );
          })}
        </div>
        <Collapse in={expandedBrowser !== null} unmountOnExit>
          {expandedBrowser !== null && (
            <ol className="mt-2 ml-5 list-decimal space-y-0.5">
              {steps[expandedBrowser].map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          )}
        </Collapse>
      </div>
    </Collapse>
  );
}
